import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { ACQUISITION_CARD_WIDTH, STEPPER_MAX_WIDTH } from '../../../theme';
import { Divider, Grid, Paper, Typography } from '@material-ui/core';
import SatelliteChooser from '../../components/acquisition/SatelliteChooser';
import ShoreSEntryChooser from '../../components/shoreS/ShoreSEntryChooser';
import ShoreSDataChooser from '../../components/shoreS/ShoreSDataChooser';
import ShoreSPeriodImageChooser from '../../components/shoreS/ShoreSPeriodImageChooser';
import WorkflowStepper from '../../components/steps/WorkflowStepper';
import StepSummary from '../../components/steps/StepSummary';
import Footer from '../../components/core/Footer';
import { Actions as Auth } from '../../../store/ducks/auth';
import { useStepNavigations, FIRST } from '../../../hooks/useStepNavigations';
import { getAcquisitionParameters } from '../../../selectors';

/**
 * Module for the shore s process
 * @module ShoreS
 */

/**
 * @function getShoreSAcquisitionParameters
 * @description Extends the shared `getAcquisitionParameters` selector with `shoreSHasTemporalData`
 * (from sessionStorage, set once the uploaded model file is parsed), the `requires` gate the
 * period/image step needs to unlock.
 * @param {Object} state - Redux state.
 * **Called by:
 * @see {useStepNavigations}
 * @memberof module:ShoreS
 * @returns {Object} Acquisition parameters plus `shoreSHasTemporalData`.
 */
const getShoreSAcquisitionParameters = (state) => ({
  ...getAcquisitionParameters(state),
  shoreSHasTemporalData: sessionStorage.getItem('shoreSHasTemporalData') === 'true' || undefined,
});

/**
 * @constant MODULE_ACCENT
 * @description ShoreS's identity color. Shared by the page header and the step indicator, so the
 * two read as one module instead of as two unrelated palettes.
 * @memberof module:ShoreS
 */
const MODULE_ACCENT = '#7952B3';

const STEPS = [
  {
    label: 'forms.shoreS.acquisition.0.title',
    content: 'forms.shoreS.acquisition.0.description',
    component: ShoreSEntryChooser,
  },
  {
    label: 'forms.acquisition.1.title',
    content: 'forms.shoreS.acquisition.1.description',
    /**
     * Wrapped rather than passed straight through: `SatelliteChooser` is shared by every module and
     * `makeRoutes` renders it with a fixed set of props, so the redesigned look is opted into here.
     */
    component: (props) => <SatelliteChooser {...props} variant="modern" />,
  },
  {
    label: 'forms.shoreS.acquisition.2.title',
    content: 'forms.shoreS.acquisition.2.description',
    header: 'forms.shoreS.acquisition.2.header',
    requires: 'satellite',
    component: ShoreSDataChooser,
  },
  {
    label: 'forms.shoreS.acquisition.periodAndImages.title',
    content: 'forms.shoreS.acquisition.periodAndImages.description',
    requires: 'shoreSHasTemporalData',
    component: ShoreSPeriodImageChooser,
  },
];

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexFlow: 'column',
    flexGrow: 1,
  },
  header: {
    backgroundColor: MODULE_ACCENT,
    padding: theme.spacing(4, 0, 12),
  },
  title: {
    color: 'white',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
  },
  content: {
    width: ACQUISITION_CARD_WIDTH,
    marginTop: theme.spacing(-9),
  },
  summary: {
    marginBottom: theme.spacing(1),
  },
  instructions: {
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  /**
   * The wizard header overlaps the card above it, so the indicator needs its own top room; the
   * max-width keeps the connectors short and centred instead of running the full card width.
   */
  stepper: {
    maxWidth: STEPPER_MAX_WIDTH,
    padding: theme.spacing(4, 3, 1.5),
  },
}));

/**
 * @function ShoreS
 * @description Shore-S's acquisition flow: entry card, satellite chooser, model file upload
 * (CRS + `.mat`/CSV parsing), and the merged period/cloud-filter/image-selection step. Same
 * stepper/routing shell (`useStepNavigations`) as every other module's acquisition page.
 * @param {Object} props - Component properties passed from the router (match, location, history)
 * @param {Object} props.match - React Router match object, with the current `step` param.
 * @see {@link ShoreSEntryChooser} - Component for the entry card that opens the flow
 * @see {@link SatelliteChooser} - Component for selecting satellite
 * @see {@link ShoreSDataChooser} - Component for uploading the model file and picking its CRS
 * @see {@link ShoreSPeriodImageChooser} - Component for selecting the period and refining the image list
 * **Called by:
 * @see {MainPage} - via the `/shore_s/:step?` Route.
 * @memberof module:ShoreS
 * @returns {JSX.Element} The Shore-S acquisition page.
 */
const ShoreS = (props) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const classes = useStyles();
  const { match } = props;
  const { acquisitionData, navigate, navigateTo, stepAttendsRequirements, canRevisitStep, makeRoutes } = useStepNavigations(
    match,
    STEPS,
    '/main/processing_shore_s',
    '',
    getShoreSAcquisitionParameters,
  );

  useEffect(() => {
    dispatch(Auth.begin());
    if (!stepAttendsRequirements()) navigate(FIRST);
  }, [dispatch]);

  const hasStep = match.params.step !== undefined;
  const step = hasStep ? match.params.step - 1 : 0;

  return (
    <div className={classes.wrapper}>
      <Grid container spacing={0} justifyContent="center">
        <Grid item xs={12} className={classes.header}>
          <Typography className={classes.title} variant="h4" align="center">
            {t(STEPS[step].header ?? 'forms.shoreS.acquisition.title')}
          </Typography>
        </Grid>
        <Grid item className={classes.container}>
          <Paper className={classes.content} elevation={1}>
            <WorkflowStepper
              className={classes.stepper}
              accentColor={MODULE_ACCENT}
              steps={STEPS.map((el, i) => ({ id: String(i), label: t(el.label), disabled: !canRevisitStep(i) }))}
              currentStep={String(step)}
              onStepClick={(i) => canRevisitStep(i) && navigateTo(i)}
            />
            <StepSummary acquisition={acquisitionData} className={classes.summary} />
            {STEPS[step].content && (
              <Typography className={classes.instructions} variant="subtitle1" align="center">
                {t(STEPS[step].content)}
              </Typography>
            )}
            <Divider />
            <Switch>{makeRoutes(hasStep)}</Switch>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

export default ShoreS;
